import { useMemo } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Star } from "lucide-react";
import { useDefaultFunctions } from "@/hooks/useDefaultFunctions";
import {
  usePersonSkillRatings,
  useUpsertSkillRating,
  useDeleteSkillRating,
} from "@/hooks/usePersonSkillRatings";
import { cn } from "@/lib/utils";

interface SkillMatrixProps {
  personId: string;
}

export function SkillMatrix({ personId }: SkillMatrixProps) {
  const { data: defaultFunctions, isLoading: loadingFunctions } =
    useDefaultFunctions();
  const { data: skillRatings, isLoading: loadingRatings } =
    usePersonSkillRatings(personId);
  const upsertRating = useUpsertSkillRating();
  const deleteRating = useDeleteSkillRating();

  const ratingMap = useMemo(() => {
    const map = new Map<
      string,
      { id: string; rating: number; notes: string | null }
    >();
    skillRatings?.forEach((sr) => {
      map.set(sr.default_function_id, {
        id: sr.id,
        rating: sr.rating,
        notes: sr.notes,
      });
    });
    return map;
  }, [skillRatings]);

  const handleRate = (defaultFunctionId: string, rating: number) => {
    const existing = ratingMap.get(defaultFunctionId);
    if (existing && existing.rating === rating) {
      // Clicking the same rating removes it
      deleteRating.mutate({ id: existing.id, personId });
    } else {
      upsertRating.mutate({
        personId,
        defaultFunctionId,
        rating,
      });
    }
  };

  if (loadingFunctions || loadingRatings) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!defaultFunctions || defaultFunctions.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Skill Matrix</CardTitle>
          <CardDescription>
            Rate this person's skills for each function.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <p>No default functions configured yet.</p>
            <p className="text-sm mt-1">
              Go to Settings &gt; Default Functions to add function templates.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Skill Matrix</CardTitle>
        <CardDescription>
          Rate this person's skills for each function (1-5 stars). Click a star
          to set, click again to remove.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-1">
          {defaultFunctions.map((fn) => {
            const existing = ratingMap.get(fn.id);
            const currentRating = existing?.rating ?? 0;

            return (
              <div
                key={fn.id}
                className="flex items-center gap-4 px-3 py-2.5 rounded-md hover:bg-muted/50 transition-colors"
              >
                <span className="flex-1 text-sm font-medium min-w-[140px]">
                  {fn.name}
                </span>
                <div className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      aria-label={`${fn.name} ${star} star${star === 1 ? "" : "s"}`}
                      className={cn(
                        "p-0.5 rounded transition-colors hover:scale-110",
                        star <= currentRating
                          ? "text-yellow-500"
                          : "text-muted-foreground/30 hover:text-yellow-400",
                      )}
                      onClick={() => handleRate(fn.id, star)}
                      disabled={
                        upsertRating.isPending || deleteRating.isPending
                      }
                    >
                      <Star
                        className="h-5 w-5"
                        fill={star <= currentRating ? "currentColor" : "none"}
                      />
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
